/**
 * ---------------------------------------------------------------------------------------------------------------------
 * COMBINED LISTINGS
 * ---------------------------------------------------------------------------------------------------------------------
 *
 * Groups sibling products (same style, one product per color, size, material...) behind a single set of selectors.
 *
 * The grouping is authored in the admin with a `product_combinations` metaobject referenced from the
 * `custom.product_combination_selector` product metafield. Liquid renders the selectors, this file makes them
 * interactive:
 *
 *   - `<combined-listing-selector>` on the product page swaps the whole product section for the selected product,
 *     which updates the gallery, title, price, sku, badges, variant picker and add to cart form in one go.
 *   - `<combined-listing-card-swatches>` inside a product card previews a sibling product without leaving the listing.
 *
 * Nothing here is specific to colors: a selector row is driven by its group's `selector_title`, so a size group behaves
 * exactly the same way.
 */

const MAIN_PRODUCT_SECTION_SELECTOR = '.shopify-section--main-product';
const APP_SECTION_SELECTOR = '.shopify-section--apps';
const GROUP_SELECTOR = '[data-combined-listing-group]';
const VALUE_SELECTOR = '[data-combined-listing-value]';
const SELECTED_LABEL_SELECTOR = '[data-combined-listing-selected-label]';
const CARD_VALUE_SELECTOR = '[data-combined-listing-card-value]';

/** Script types that must never be re-executed when a fetched section is inserted in the page. */
const INERT_SCRIPT_TYPES = new Set(['application/json', 'application/ld+json', 'importmap', 'speculationrules', 'text/template']);

/** Returns true when a click should be left to the browser (new tab, middle click, download...). */
function isModifiedClick(event) {
  return event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
}

/**
 * Fetches product pages once and keeps the parsed documents around, so going back and forth between the members of a
 * group is instant. Requests can be warmed up ahead of the click with `prefetch`.
 */
class ProductPageCache {
  #documents = new Map();
  #requests = new Map();

  get(url) {
    const key = this.#key(url);
    const cached = this.#documents.get(key);

    if (cached) {
      return Promise.resolve(cached);
    }

    const pending = this.#requests.get(key);

    if (pending) {
      return pending;
    }

    const request = fetch(key)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Combined listing: ${key} responded with ${response.status}`);
        }

        return response.text();
      })
      .then((html) => {
        const document = new DOMParser().parseFromString(html, 'text/html');
        this.#documents.set(key, document);

        return document;
      });

    this.#requests.set(key, request);
    request.catch(() => {}).then(() => this.#requests.delete(key));

    return request;
  }

  prefetch(url) {
    this.get(url).catch(() => {});
  }

  /** The query string is kept so a URL pointing at a specific variant is never served the bare product page. */
  #key(url) {
    const { pathname, search } = new URL(url, window.location.origin);

    return `${pathname}${search}`;
  }
}

/**
 * Replaces the main product section with the one of another product of the group. Every custom element of the theme
 * upgrades itself when inserted, so the swapped markup behaves exactly like a freshly loaded page.
 */
class MainProductSwapper {
  #cache = new ProductPageCache();
  #requestId = 0;
  #hasSwapped = false;
  #liveRegion;

  get hasSwapped() {
    return this.#hasSwapped;
  }

  get section() {
    return document.querySelector(MAIN_PRODUCT_SECTION_SELECTOR);
  }

  prefetch(url) {
    if (this.section) {
      this.#cache.prefetch(url);
    }
  }

  /**
   * @param {string} url the product URL to display
   * @param {{ updateHistory?: boolean }} options
   * @returns {Promise<boolean>} whether the section could be swapped in place
   */
  async swap(url, { updateHistory = true } = {}) {
    const section = this.section;

    if (!section) {
      return false;
    }

    // Guards against a slow request overwriting the result of a more recent click
    const requestId = ++this.#requestId;

    section.setAttribute('aria-busy', 'true');

    let incomingDocument;

    try {
      incomingDocument = await this.#cache.get(url);
    } catch (error) {
      console.error(error);
      window.location.assign(url);

      return false;
    }

    if (requestId !== this.#requestId || !section.isConnected) {
      return false;
    }

    const incomingSection = incomingDocument.querySelector(MAIN_PRODUCT_SECTION_SELECTOR);

    if (!incomingSection) {
      window.location.assign(url);

      return false;
    }

    const nextSection = document.importNode(incomingSection, true);
    const alreadyLoadedSources = new Set(Array.from(document.querySelectorAll('script[src]'), (script) => script.src));
    const focusedValue = document.activeElement?.closest?.(VALUE_SELECTOR);
    const focusedProductId = section.contains(focusedValue) ? focusedValue.dataset.productId : null;

    section.removeAttribute('aria-busy');
    section.replaceWith(nextSection);

    if (focusedProductId) {
      this.#restoreFocus(nextSection, focusedProductId);
    }

    this.#announce(nextSection);
    this.#executeScripts(nextSection, alreadyLoadedSources);
    const replacedAppSections = this.#replaceProductAppSections(incomingDocument, alreadyLoadedSources);
    this.#updateMetadata(incomingDocument);

    if (updateHistory) {
      window.history.pushState({ combinedListing: true }, '', this.#urlWithVariant(nextSection, url));
    }

    this.#hasSwapped = true;

    window.Shopify?.PaymentButton?.init?.();

    this.#dispatchSectionEvents(nextSection, url);

    for (const appSection of replacedAppSections) {
      this.#dispatchSectionEvents(appSection, url);
    }

    return true;
  }

  /**
   * Junip's full review widget lives in a separate `apps` section below the main product section. Replace every Junip
   * app section with the markup rendered for the selected sibling product, then allow the app to initialize it again.
   */
  #replaceProductAppSections(incomingDocument, alreadyLoadedSources) {
    const isJunipSection = (section) => section.innerHTML.toLowerCase().includes('junip');
    const currentSections = Array.from(document.querySelectorAll(APP_SECTION_SELECTOR)).filter(isJunipSection);
    const incomingSections = Array.from(incomingDocument.querySelectorAll(APP_SECTION_SELECTOR)).filter(isJunipSection);
    const replacements = [];

    currentSections.forEach((currentSection, index) => {
      const incomingSection =
        incomingSections.find((candidate) => candidate.id === currentSection.id) ?? incomingSections[index];

      if (!incomingSection) {
        return;
      }

      const nextSection = document.importNode(incomingSection, true);
      currentSection.replaceWith(nextSection);
      this.#executeScripts(nextSection, alreadyLoadedSources);
      replacements.push(nextSection);
    });

    return replacements;
  }

  /**
   * The focused value is removed along with the old section, so focus is moved to the same value in the new one (or to
   * the product title) instead of falling back to the top of the document.
   */
  #restoreFocus(section, productId) {
    const value = section.querySelector(`${VALUE_SELECTOR}[data-product-id="${productId}"]`);

    if (value) {
      value.focus();

      return;
    }

    const title = section.querySelector('.product-title');

    if (title) {
      title.setAttribute('tabindex', '-1');
      title.focus({ preventScroll: true });
    }
  }

  /**
   * Screen readers only announce changes to a live region they already know about, so the empty region is added as soon
   * as a selector is on the page, well before the first swap writes to it.
   */
  ensureLiveRegion() {
    if (this.#liveRegion?.isConnected) {
      return;
    }

    this.#liveRegion = document.createElement('div');
    this.#liveRegion.className = 'sr-only';
    this.#liveRegion.setAttribute('role', 'status');
    this.#liveRegion.setAttribute('aria-live', 'polite');
    document.body.append(this.#liveRegion);
  }

  /** Tells screen reader users which product the page now shows, since the rest of the section changed silently. */
  #announce(section) {
    const title = section.querySelector('.product-title')?.textContent.trim();

    if (!title || !this.#liveRegion?.isConnected) {
      return;
    }

    this.#liveRegion.textContent = title;
  }

  #dispatchSectionEvents(section, url) {
    section.dispatchEvent(
      new CustomEvent('shopify:section:load', {
        bubbles: true,
        detail: { sectionId: section.id.replace('shopify-section-', '') },
      })
    );

    section.dispatchEvent(new CustomEvent('combined-listing:change', { bubbles: true, detail: { url } }));
  }

  /**
   * Scripts coming from a parsed document are inert, so they are recreated to run. External scripts already present in
   * the page are skipped to avoid initializing an app twice.
   */
  #executeScripts(root, alreadyLoadedSources) {
    root.querySelectorAll('script').forEach((inertScript) => {
      const type = (inertScript.getAttribute('type') || '').toLowerCase();

      if (INERT_SCRIPT_TYPES.has(type)) {
        return;
      }

      const source = inertScript.getAttribute('src');

      if (source && alreadyLoadedSources.has(new URL(source, window.location.href).href)) {
        return;
      }

      const script = document.createElement('script');

      for (const { name, value } of inertScript.attributes) {
        script.setAttribute(name, value);
      }

      script.textContent = inertScript.textContent;
      inertScript.replaceWith(script);
    });
  }

  #updateMetadata(incomingDocument) {
    if (incomingDocument.title) {
      document.title = incomingDocument.title;
    }

    const incomingCanonical = incomingDocument.querySelector('link[rel="canonical"]');
    const canonical = document.querySelector('link[rel="canonical"]');

    if (incomingCanonical && canonical) {
      canonical.href = incomingCanonical.href;
    }
  }

  /** Keeps the address bar in sync with the variant that the swapped section actually selected. */
  #urlWithVariant(section, url) {
    const target = new URL(url, window.location.origin);
    const variantData = section.querySelector('variant-picker script[data-variant]');

    if (variantData) {
      try {
        const variant = JSON.parse(variantData.textContent);

        if (variant?.id) {
          target.searchParams.set('variant', variant.id);
        }
      } catch {
        // The section is still usable without the variant in the URL
      }
    }

    return `${target.pathname}${target.search}`;
  }
}

const swapper = new MainProductSwapper();

window.addEventListener('popstate', () => {
  if (swapper.hasSwapped) {
    swapper.swap(window.location.href, { updateHistory: false });
  }
});

/**
 * Product page selector. One instance holds every group of the product. Values are links, marked with `aria-current`
 * for the product being displayed, so Tab and Enter work as they do for any link.
 */
class CombinedListingSelector extends HTMLElement {
  connectedCallback() {
    swapper.ensureLiveRegion();
    this.addEventListener('click', this.#onClick);
    this.addEventListener('pointerenter', this.#onIntent, { capture: true });
    this.addEventListener('focusin', this.#onIntent);
    this.addEventListener('touchstart', this.#onIntent, { passive: true });
  }

  /** Warms up the page of the value the customer is about to pick, so the swap feels instant. */
  #onIntent = (event) => {
    const value = event.target.closest?.(VALUE_SELECTOR);

    if (value && this.contains(value)) {
      swapper.prefetch(value.dataset.productUrl);
    }
  };

  #onClick = (event) => {
    const value = event.target.closest(VALUE_SELECTOR);

    if (!value || !this.contains(value) || isModifiedClick(event)) {
      return;
    }

    event.preventDefault();
    this.select(value);
  };

  /**
   * @param {HTMLElement} value the swatch or block to activate
   */
  async select(value) {
    if (value.hasAttribute('aria-current')) {
      return;
    }

    // The selected state is applied straight away so the UI never feels like it lags behind the click
    this.#markSelected(value);

    await swapper.swap(value.dataset.productUrl);
  }

  #markSelected(value) {
    const group = value.closest(GROUP_SELECTOR);

    for (const sibling of this.#valuesOf(value)) {
      const isSelected = sibling === value;

      sibling.classList.toggle('is-selected', isSelected);
      if (isSelected) {
        sibling.setAttribute('aria-current', 'page');
      } else {
        sibling.removeAttribute('aria-current');
      }
    }

    const selectedLabel = group?.querySelector(SELECTED_LABEL_SELECTOR);

    if (selectedLabel) {
      selectedLabel.textContent = value.dataset.label ?? '';
    }
  }

  #valuesOf(value) {
    const group = value.closest(GROUP_SELECTOR) ?? this;

    return Array.from(group.querySelectorAll(VALUE_SELECTOR));
  }
}

/**
 * Product card swatches. Picking a value previews the sibling product inside the card (media, title, price and links)
 * without navigating, so a whole group reads as a single card in listings.
 */
class CombinedListingCardSwatches extends HTMLElement {
  /** Handle of the product the card was rendered for, i.e. the main product of the group. */
  #mainHandle;

  connectedCallback() {
    this.#mainHandle = this.querySelector('[aria-current]')?.dataset.productHandle;
    this.addEventListener('click', this.#onClick);
  }

  #onClick = (event) => {
    const value = event.target.closest(CARD_VALUE_SELECTOR);

    if (!value || !this.contains(value) || isModifiedClick(event)) {
      return;
    }

    event.preventDefault();
    this.select(value);
  };

  select(value) {
    if (value.hasAttribute('aria-current')) {
      return;
    }

    for (const sibling of this.querySelectorAll(CARD_VALUE_SELECTOR)) {
      const isSelected = sibling === value;

      sibling.classList.toggle('is-selected', isSelected);
      if (isSelected) {
        sibling.setAttribute('aria-current', 'true');
      } else {
        sibling.removeAttribute('aria-current');
      }
    }

    const card = this.closest('product-card');

    if (!card) {
      return;
    }

    this.#updateMedia(card, value);
    this.#updateInfo(card, value);
    this.#updateQuickBuy(card, value);
  }

  #updateMedia(card, { dataset }) {
    this.#applyImage(
      card.querySelector('.product-card__image--primary'),
      dataset.image,
      dataset.imageSrcset,
      dataset.imageAlt
    );

    const secondary = card.querySelector('.product-card__image--secondary');

    if (secondary) {
      // Products with a single image keep showing the primary one on hover rather than a stale sibling image
      secondary.hidden = !dataset.secondaryImage;
      this.#applyImage(secondary, dataset.secondaryImage, dataset.secondaryImageSrcset, dataset.secondaryImageAlt);
    }
  }

  #applyImage(image, src, srcset, alt) {
    if (!image || !src) {
      return;
    }

    image.src = src;
    image.srcset = srcset ?? '';
    image.alt = alt ?? '';
    image.removeAttribute('data-src');
    image.removeAttribute('data-srcset');
  }

  #updateInfo(card, { dataset }) {
    for (const link of card.querySelectorAll('.product-card__media, .product-title')) {
      link.href = dataset.productUrl;
    }

    const title = card.querySelector('.product-title');

    if (title && dataset.productTitle) {
      title.textContent = dataset.productTitle;
    }

    const priceList = card.querySelector('price-list');

    if (priceList && dataset.priceHtml) {
      priceList.outerHTML = dataset.priceHtml;
    }

    const ratingBadge = card.querySelector('.rating-badge');

    if (dataset.ratingHtml) {
      if (ratingBadge) {
        ratingBadge.outerHTML = dataset.ratingHtml;
      } else {
        card.querySelector('price-list')?.insertAdjacentHTML('afterend', dataset.ratingHtml);
      }
    } else {
      ratingBadge?.remove();
    }

    card.setAttribute('handle', dataset.productHandle);
  }

  /** Keeps the quick add controls pointing at the previewed product rather than the main one. */
  #updateQuickBuy(card, { dataset }) {
    const isMain = dataset.productHandle === this.#mainHandle;
    const isAvailable = dataset.available === 'true';

    card.querySelector('quick-buy-modal')?.setAttribute('product-url', dataset.productUrl);

    for (const input of card.querySelectorAll('.product-card__quick-buy input[name="id"]')) {
      if (dataset.variantId) {
        input.value = dataset.variantId;
      }
    }

    const quickBuy = card.querySelector('.product-card__quick-buy');

    if (quickBuy) {
      quickBuy.hidden = !isAvailable;
    }

    // The size shortcuts are built from the main product variants, so they no longer apply to a sibling
    const sizeSelector = card.querySelector('.product-card__floating-size-selector');

    if (sizeSelector) {
      sizeSelector.hidden = !isMain;
    }
  }
}

if (!window.customElements.get('combined-listing-selector')) {
  window.customElements.define('combined-listing-selector', CombinedListingSelector);
}

if (!window.customElements.get('combined-listing-card-swatches')) {
  window.customElements.define('combined-listing-card-swatches', CombinedListingCardSwatches);
}
